<template>
  <EButton
    v-bind="$attrs"
    :size="size"
    :class="classes"
    class="icon-text-button"
    v-on="$listeners"
  >
    <span v-if="iconPosition === 'end'" data-test-id="text" class="icon-text-button__text">
      <slot />
    </span>
    <EIcon
      :icon="icon"
      :size="iconSize"
      class="icon-text-button__icon"
      data-test-id="icon"
      :class="iconClasses"
    />
    <span v-if="iconPosition === 'start'" data-test-id="text" class="icon-text-button__text">
      <slot />
    </span>
  </EButton>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

import EButton from '~~/common/vue/components/button';
import EIcon from '~~/common/vue/components/icon';

export const iconPositions = ['start', 'end'];

export default {
  name: 'EIconTextButton',
  components: {
    EButton,
    EIcon,
  },
  mixins: [classes],
  props: {
    icon: {
      type: String,
      required: true,
    },
    iconSize: {
      type: String,
      default: 'm',
    },
    /**
     * one of `start`, `end`
     */
    iconPosition: {
      type: String,
      default: 'start',
      cssClass: true,
      validator: (position) => iconPositions.includes(position),
    },
    /**
     * Whether text should be move below icon on mobile
     */
    mobileStack: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    size: {
      type: String,
      default: 'm',
    },
  },
  computed: {
    iconClasses() {
      const prefix = 'icon-text-button__icon';
      return [
        `${prefix}--icon-${this.icon}`,
        `${prefix}--size-${this.iconSize}`,
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.icon-text-button__text {
  padding-top: $space-2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-text-button__icon {
  display: block;

  .icon-text-button--icon-position-start & {
    margin-right: $space-2s;
    margin-left: -$space-1s;
  }

  .icon-text-button--icon-position-end & {
    margin-right: -$space-1s;
    margin-left: $space-2s;
  }
}

.icon-text-button--mobile-stack {
  @include tablet-down {
    flex-direction: column;
    // TODO: override height set by Button; find a better solution
    height: auto;
    padding-top: $space-s;
    padding-bottom: $space-s;

    .icon-text-button__icon {
      margin-right: 0;
      margin-left: 0;
      transform: scale($scale-5l);
    }

    .icon-text-button__text {
      padding-top: $space-1s;
    }
  }
}
</style>

<docs>
Default:

```js
<div>
  <EIconTextButton icon="source-google-maps">Directions</EIconTextButton>
  <EIconTextButton icon="close">Close</EIconTextButton>
  <EIconTextButton icon="chevron-left" icon-size="s">Back</EIconTextButton>
</div>
```

With stack icon and text on mobile screen width:

```js
<EIconTextButton icon="source-google-maps" icon-size="m" mobile-stack>Re-center</EIconTextButton>
```

With varying text size:

```js
<EIconTextButton icon="close" text-size="s">Close</EIconTextButton>
<EIconTextButton icon="close" text-size="m">Close</EIconTextButton>
```
</docs>
